import 'reflect-metadata';
import {
    applyLauncherContextFromConfig,
    buildRuntimeConfigFromClientEntry,
    invokeAtemoyaGame,
    loadGameModule,
    parseGameIdFromClientEntry,
    resolveAssetRootsFromClientEntry,
    type GameLaunchOpenContext,
    type GameRuntimeConfig,
    type LaunchGameCleanup,
} from 'atemoya-contracts';
import { createGameTransport, type GameTransportConfig } from './transport/createGameTransport';

const LAUNCH_OPEN_CONTEXTS = new Set<GameLaunchOpenContext>(['modal', 'new-tab', 'same-tab']);

export type LaunchGameOptions = {
    transportDefaults?: Partial<GameTransportConfig>;
    onGameReady?: () => void;
    isStale?: () => boolean;
};

function withLaunchContextFromGameUrl(gameUrl: string, config: GameRuntimeConfig): GameRuntimeConfig {
    try {
        const openContext = new URL(gameUrl, window.location.origin).searchParams.get('open_context');
        if (!openContext || !LAUNCH_OPEN_CONTEXTS.has(openContext as GameLaunchOpenContext)) {
            return config;
        }

        return {
            ...config,
            options: {
                ...config.options,
                openContext: openContext as GameLaunchOpenContext,
            },
        };
    } catch {
        return config;
    }
}

function buildRuntimeConfigFromGameUrl(gameUrl: string): GameRuntimeConfig {
    return (
        buildRuntimeConfigFromClientEntry(gameUrl) ?? {
            gameId: parseGameIdFromClientEntry(gameUrl) ?? '',
            assetRoots: resolveAssetRootsFromClientEntry(gameUrl),
        }
    );
}

function resolveIndexUrl(gameUrl: string): string {
    if (gameUrl.includes('index.html')) {
        return gameUrl;
    }
    const base = gameUrl.replace(/\/$/, '');
    return `${base}/index.html`;
}

function createInnerRoot(rootElement: HTMLElement): HTMLElement {
    rootElement.replaceChildren();
    const innerRoot = document.createElement('div');
    innerRoot.style.width = '100%';
    innerRoot.style.height = '100%';
    rootElement.appendChild(innerRoot);
    return innerRoot;
}

export async function launchGame(
    rootElement: HTMLElement,
    gameUrl: string,
    options: LaunchGameOptions = {}
): Promise<LaunchGameCleanup> {
    const baseConfig = buildRuntimeConfigFromClientEntry(gameUrl);
    const config = withLaunchContextFromGameUrl(
        gameUrl,
        baseConfig ?? buildRuntimeConfigFromGameUrl(gameUrl)
    );

    if (!config.gameId) {
        throw new Error(`[launchGame] Could not resolve game id from URL: ${gameUrl}`);
    }

    applyLauncherContextFromConfig(config);

    const innerRoot = createInnerRoot(rootElement);
    const transport = createGameTransport({
        gameName: config.gameId,
        ...options.transportDefaults,
    });

    const indexUrl = resolveIndexUrl(gameUrl);
    const { moduleUrl, cleanup: moduleCleanup } = await loadGameModule(indexUrl);

    if (options.isStale?.()) {
        moduleCleanup();
        transport.disconnect?.();
        return async () => {};
    }

    const gameCleanup = await invokeAtemoyaGame(moduleUrl, innerRoot, {
        config,
        services: {
            transport,
            onGameReady: options.onGameReady,
        },
    });

    if (options.isStale?.()) {
        await gameCleanup();
        moduleCleanup();
        return async () => {};
    }

    void transport.connect();

    return async () => {
        await gameCleanup();
        moduleCleanup();
        rootElement.replaceChildren();
        transport.disconnect?.();
    };
}
